import { useState, useEffect, useCallback } from 'react';
import { GetExecutionLegDetail } from '../apis/shipment';
import type { ExecutionLegDetail } from '../apis/types';
import useUnauthorize from './useUnauthorize';
import { useToken } from '../common/helper';
import { useSpin } from '../components/SpinContext';

interface UseFetchExecutionLegDetailReturn {
  detail: ExecutionLegDetail | undefined;
  isLoading: boolean;
  error: string | null;
  refetchExecutionLegDetail: (runSpin?: boolean) => void;
}

/**
 * Custom hook to fetch and manage single Execution Leg detail data
 *
 * @param elId - The ID of the execution leg to fetch
 * @param enabled - Whether fetching is active (default: true)
 * @param isSpin - Whether to show global loading spinner during fetch (default: false)
 */
const useFetchExecutionLegDetail = (
  elId?: string | null,
  enabled = true,
  isSpin = false
): UseFetchExecutionLegDetailReturn => {
  const { showSpin, hideSpin } = useSpin();
  const { checkTokenExpiry } = useToken();
  const { unauthorize } = useUnauthorize();

  const [detail, setDetail] = useState<ExecutionLegDetail>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(
    (runSpin = isSpin) => {
      if (!elId || !enabled) {
        setDetail(undefined);
        setIsLoading(false);
        setError(null);
        return;
      }

      if (runSpin) {
        showSpin();
      }
      setIsLoading(true);
      setError(null);

      const token = checkTokenExpiry();
      if (!token) {
        setIsLoading(false);
        if (runSpin) hideSpin();
        return;
      }

      GetExecutionLegDetail(token, elId)
        .then((res) => {
          setDetail(res);
        })
        .catch((err) => {
          setError('Failed to load execution leg details.');
          unauthorize(err);
          setDetail(undefined);
        })
        .finally(() => {
          setIsLoading(false);
          hideSpin();
        });
    },
    [elId, enabled, isSpin, checkTokenExpiry, showSpin, hideSpin, unauthorize]
  );

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return {
    detail,
    isLoading,
    error,
    refetchExecutionLegDetail: fetchDetail,
  };
};

export default useFetchExecutionLegDetail;
